import { Component, OnDestroy, OnInit } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { TranslateService } from '@ngx-translate/core';
import { GermplasmService } from '../../shared/germplasm/service/germplasm.service';
import { GermplasmDetailsContext } from '../germplasm-details.context';
import { GermplasmDto, GermplasmName } from '../../shared/germplasm/model/germplasm.model';
import { GermplasmDetailsUrlService } from '../../shared/germplasm/service/germplasm-details.url.service';
import { Router } from '@angular/router';
import { GermplasmNameContext } from '../../entities/germplasm/name/germplasm-name.context';
import { Subscription } from 'rxjs';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EDIT_GERMPLASM_PERMISSION, GERMPLASM_AUDIT_PERMISSION, VIEW_NAMES_PERMISSION } from '../../shared/auth/permissions';
import { UrlService } from '../../shared/service/url.service';
import { ParamContext } from '../../shared/service/param.context';
import { TruncateWithEllipsisPipe } from '../../shared/util/truncate-with-ellipsis.pipe';
import { ProgramService } from '../../shared/program/service/program.service';
import { GermplasmDetailsPopupService } from '../germplasm-details-popup.service';
import { AlertService } from '../../shared/alert/alert.service';
import { MAX_NAME_DISPLAY_SIZE } from '../../app.constants';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

@Component({
    selector: 'jhi-basic-details-pane',
    templateUrl: './basic-details-pane.component.html'
})
export class BasicDetailsPaneComponent implements OnInit, OnDestroy {

    GERMPLASM_AUDIT_PERMISSION = GERMPLASM_AUDIT_PERMISSION;

    EDIT_BASIC_DETAILS_PERMISSIONS = [...EDIT_GERMPLASM_PERMISSION, 'MODIFY_BASIC_DETAILS'];
    EDIT_BASIC_DETAILS_ACTIONS_PERMISSIONS = [...this.EDIT_BASIC_DETAILS_PERMISSIONS, ...GERMPLASM_AUDIT_PERMISSION];

    VIEW_NAMES_PERMISSION = VIEW_NAMES_PERMISSION;
    MODIFY_NAMES_PERMISSIONS = [...EDIT_GERMPLASM_PERMISSION, 'MODIFY_NAMES'];
    NAMES_ACTIONS_PERMISSIONS = [...this.MODIFY_NAMES_PERMISSIONS, ...GERMPLASM_AUDIT_PERMISSION];

    MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;

    subscriptionManager: SubscriptionsManager = new SubscriptionsManager();
    eventSubscriber: Subscription;
    germplasm: GermplasmDto;
    geojson: any;

    constructor(public languageservice: JhiLanguageService,
                public translateService: TranslateService,
                private eventManager: JhiEventManager,
                private germplasmService: GermplasmService,
                public germplasmDetailsContext: GermplasmDetailsContext,
                public germplasmDetailsUrlService: GermplasmDetailsUrlService,
                public urlService: UrlService,
                private modalService: NgbModal,
                private router: Router,
                private germplasmNameContext: GermplasmNameContext,
                public paramContext: ParamContext,
                public programService: ProgramService,
                public germplasmDetailsPopupService: GermplasmDetailsPopupService,
                private alertService: AlertService) {
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.loadGermplasm();
        this.registerGermplasmNameChanged();
    }

    registerGermplasmNameChanged() {
        this.subscriptionManager.add = this.eventManager.subscribe('basicDetailsChanged', (event) => {
            this.loadGermplasm();
            this.germplasmDetailsContext.notifyGermplasmDetailChanges();
        });
    }

    loadGermplasm(): void {
        this.germplasmService.getGermplasmById(this.germplasmDetailsContext.gid).toPromise().then((value) => {
            this.germplasm = value.body;
            if (this.germplasm.germplasmOrigins.length) {
                const germplsamOriginsFiltered = this.germplasm.germplasmOrigins.filter((g) => g.observationUnitId);
                const immediateSource = germplsamOriginsFiltered.find((g) => g.progenitorType === 'Immediate source');
                if (immediateSource) {
                    this.germplasm.germplasmOrigins = [immediateSource];
                } else {
                    this.germplasm.germplasmOrigins = germplsamOriginsFiltered;
                }
            }
            this.germplasmDetailsContext.germplasm = this.germplasm;
        });
    }

    editGermplasmBasicDetails(): void {
        this.germplasmDetailsContext.germplasm = this.germplasm;
        this.router.navigate(['/', {
            outlets: {
                popup: null
            },
        }], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.router.navigate(['/', { outlets: { popup: 'germplasm-edit-basic-details' }, }], {
                queryParamsHandling: 'merge',
                skipLocationChange: true,
            });
        });

    }

    editGermplasmName(germplasmName: GermplasmName): void {
        this.germplasmNameContext.germplasmName = germplasmName;
        this.router.navigate(['/', {
            outlets: {
                popup: null
            },
        }], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.router.navigate(['/', { outlets: { popup: 'germplasm-name-dialog/' + this.germplasm.gid }, }], {
                queryParamsHandling: 'merge',
                skipLocationChange: true,
            });
        });
    }

    createGermplasmName(): void {
        this.router.navigate(['/', {
            outlets: {
                popup: null
            }
        }], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.router.navigate(['/', { outlets: { popup: 'germplasm-name-dialog/' + this.germplasm.gid }, }], {
                queryParamsHandling: 'merge',
                skipLocationChange: true
            });
        });
    }

    deleteGermplasmName(germplasmName: GermplasmName): void {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        const truncateWithEllipsisPipe = new TruncateWithEllipsisPipe();
        confirmModalRef.componentInstance.message = this.translateService.instant('germplasm-name-modal.delete.warning',
            { param: truncateWithEllipsisPipe.transform(germplasmName.name, this.MAX_NAME_DISPLAY_SIZE) });

        confirmModalRef.result.then(() => {
            this.germplasmService.deleteGermplasmName(this.germplasm.gid, germplasmName.id).toPromise().then((result) => {
                this.alertService.success('germplasm-name-modal.delete.success');
                this.loadGermplasm();
                this.germplasmDetailsContext.notifyGermplasmDetailChanges();
            });
        }, () => confirmModalRef.dismiss());
    }

    isStudyClickable(studyProgramUUID: string) {
        return this.paramContext.programUUID === studyProgramUUID;
    }

    openGermplasmNameAuditModal(germplasmName: GermplasmName): void {
        this.router.navigate(['/', {
            outlets: {
                popup: null
            }
        }], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.germplasmNameContext.germplasmName = germplasmName;
            this.router.navigate(['/', { outlets: { popup: `germplasm/${this.germplasm.gid}/names/${germplasmName.id}/audit-dialog` }, }], {
                queryParamsHandling: 'merge'
            });
        });
    }

    openGermplasmBasicDetailsAuditModal(): void {
        this.router.navigate(['/', {
            outlets: {
                popup: null
            }
        }], { skipLocationChange: true, queryParamsHandling: 'merge' }).then(() => {
            this.router.navigate(['/', { outlets: { popup: `germplasm/${this.germplasm.gid}/basic-details/audit-dialog` }, }], {
                queryParamsHandling: 'merge'
            });
        });
    }

    hasGeoCoordinates() {
        return this.hasGermplasmOrigin() && this.germplasm.germplasmOrigins.some((origin) => origin.geoCoordinates != null);
    }

    hasGermplasmOrigin() {
        return !!(this.germplasm && this.germplasm.germplasmOrigins.length);
    }

    geoCoordinates() {
        return this.germplasm.germplasmOrigins.filter((origin) => origin.geoCoordinates != null).map((origin) => origin.geoCoordinates) || [];
    }

    openStudy(studyId: number, studyName: string) {
        this.programService.getProgramByProgramUUID(this.paramContext.cropName,  this.paramContext.programUUID).toPromise().then((responseProgram) => {
            if (this.germplasmDetailsPopupService) {
                this.germplasmDetailsPopupService.close();
            }
            this.urlService.openStudy(studyId, studyName, responseProgram.body);
        });
    }
}
